import clsx from 'clsx'
import isString from 'lodash/isString'
import noop from 'lodash/noop'
import React, { FC, useCallback, useEffect, useState } from 'react'

import SvgClose from '@Assets/SVG/Close'
import { useTranslation } from '@Globals/Translations'
import { EmailRegexValidation } from '@Shared/Components/Forms/Validations/EmailValidation'
import { useEmailSuggestion } from '@Shared/Hooks/Inputs/useEmailSuggestion'

import styles from './EmailSuggestion.module.scss'

export interface EmailSuggestionProps {
  checkForID?: string
  className?: string
  email: string
  onClick: (email: string) => void
}

export const EmailSuggestion: FC<EmailSuggestionProps> = ({ checkForID, className, email, onClick }) => {
  const { t } = useTranslation('validation')
  const [canceled, setCanceled] = useState(false)
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null)
  const { fetchEmailSuggestEmail } = useEmailSuggestion()
  const [hasFocus, setHasFocus] = useState(false)

  const updateSuggestion = useCallback(async (mail?: string | null) => {
    if (!isString(mail)) {
      setSuggestedEmail(null)
    }
    const res = await fetchEmailSuggestEmail(mail)
    if (res?.suggestion) {
      setSuggestedEmail(res.suggestion)
    } else {
      setSuggestedEmail(null)
    }
  }, [fetchEmailSuggestEmail])

  useEffect(() => {
    if (EmailRegexValidation(email)) {
      updateSuggestion(email)
    }
  }, [email, updateSuggestion])

  useEffect(() => {
    if (checkForID) {
      const focus = () => setHasFocus(true)
      const blur = () => {
        setTimeout(() => {
          setHasFocus(false)
        }, 100)
      }
      const element = document.getElementById(checkForID)
      if (element) {
        element?.addEventListener('focus', focus)
        element?.addEventListener('blur', blur)
        return () => {
          element.removeEventListener('focus', focus)
          element.removeEventListener('blur', blur)
        }
      }
    }
    return noop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!suggestedEmail || !hasFocus || canceled) {
    return null
  }

  return (
    <div className={clsx(styles.emailSuggestion, className)}>
      <button
        // TODO: use translation for aria-label
        aria-label="Close"
        className={styles.close}
        onClick={() => {
          setSuggestedEmail(null)
          setCanceled(true)
        }}
        type="button"
      >
        <SvgClose />
      </button>
      <button
        className={styles.wrapper}
        onClick={() => {
          if (suggestedEmail) {
            onClick(suggestedEmail)
          }
          setCanceled(true)
        }}
        type="button"
      >
        <span className={styles.header}>{t('COF_EMAIL_DID_YOU_MEAN_X', 'Did you mean', { arg1: '' })}</span>
        <span className={styles.suggestion} dir="rtl">{suggestedEmail}</span>
      </button>

    </div>
  )
}
